import React from 'react';

import PropTypes from '+/instances/propTypesInstance';
import { Field } from '+/components/fields';
import Theme from '+/instances/Theme';
import { ExitPrompt } from '+/components/ExitPrompt';
import { StyledForm, StyledErrorMessage, StyledFormGroup } from '+/styledComponents/elements/forms';
import { StyledH3, StyledH6, StyledP } from '+/styledComponents/elements/typography';
import { CheckboxList } from '+/components/CheckboxList';
import StyledCard from '+/styledComponents/StyledCard';
import StyledButton from '+/styledComponents/StyledButtons';
import TextReader from '+/utils/TextReader';
import StyledRow from '+/styledComponents/StyledRow';
import { StyledContainer } from '+/styledComponents/elements/containers';
import { getActionOptions } from '+/utils/getActionOptions';
import { useSyncCustomActionTypes } from '+/utils/hooks/useEnabledCustoms';
import StyledInputBlock from '+/styledComponents/StyledInputBlock';

const { t } = new TextReader('preferences');
const { t: tN } = new TextReader('notifications');

const getBrowserNotifPermission = () => {
  if (typeof window === 'undefined' || !window.Notification || !navigator.serviceWorker) {
    return 'unsupported';
  }
  return window.Notification.permission;
};

const clientOptions = t('preferredEmailClient.clientSelectorOptions');

const EMAIL_CLIENT_OPTIONS = [
  { label: clientOptions.appleMail, value: 'apple mail' },
  { label: clientOptions.gmail, value: 'gmail' },
  { label: clientOptions.outlookBrowser, value: 'outlook (browser)' },
  { label: clientOptions.outlookDesktop, value: 'outlook (desktop)' },
  { label: clientOptions.thunderbird, value: 'thunderbird' },
  { label: clientOptions.yahoo, value: 'yahoo' },
].sort((a, b) => (a.label < b.label ? -1 : 1));

export const PreferencesForm = ({ handleSubmit, isValid, allActionTypeTemplates, allActionTypeMappings, setValues, ...props }) => {
  const { optionalOptions, requiredOptions } = getActionOptions(allActionTypeMappings, allActionTypeTemplates, 'preferences');

  // Track custom enabled
  useSyncCustomActionTypes({
    allActionTypeTemplates,
    values: props.values,
    setValues,
  });

  const browserPermission = getBrowserNotifPermission();
  const browserDisabled = browserPermission === 'denied' || browserPermission === 'unsupported';
  let browserNote;
  if (browserPermission === 'denied') browserNote = tN('fields.browserNotifications.deniedNote');
  else if (browserPermission === 'unsupported') browserNote = tN('fields.browserNotifications.unsupportedNote');

  return (
    <StyledContainer narrow center>
      <Theme light>
        <StyledForm onSubmit={handleSubmit}>
          <ExitPrompt shouldPrompt={isValid && props.dirty} message={location => t('exitPrompt', { path: location.pathname })} />
          <StyledCard.Box autoHeight>
            <StyledH3>{t('title')}</StyledH3>
            <br />
            {optionalOptions.length ? (
              <>
                <StyledH6 textLeft>{t('optionalActionsHeader')}</StyledH6>
                <CheckboxList {...props} boxes={optionalOptions} />
              </>
            ) : null}
            <StyledErrorMessage>{props.errors.somethingEnabled}</StyledErrorMessage>
            <br />

            {requiredOptions.length ? (
              <>
                <StyledH6 textLeft>{t('requiredActionsHeader')}</StyledH6>
                <StyledP small>{t('requiredActionsText')}</StyledP>
                <CheckboxList allowSelectAll={false} autoSelected {...props} boxes={requiredOptions} />
                <StyledErrorMessage>{props.errors.somethingEnabled}</StyledErrorMessage>
              </>
            ) : null}

            <br />
            <Field {...props} name="emailClient" label={t('preferredEmailClient.label')} fieldType="select" options={EMAIL_CLIENT_OPTIONS} />
          </StyledCard.Box>
          <br />
          <StyledCard.Box autoHeight>
            <StyledFormGroup>
              <StyledH3 textLeft>{tN('sections.emailNotifications')}</StyledH3>
              <StyledInputBlock
                {...props}
                name="newActionEmailNotificationsEnabled"
                label={tN('fields.emailNotifications.label')}
                fieldType="checkbox"
                checkToggle
              />
            </StyledFormGroup>
            <StyledFormGroup>
              <StyledH3 textLeft>{tN('sections.browserNotifications')}</StyledH3>
              <StyledInputBlock
                {...props}
                name="newActionBrowserNotificationsEnabled"
                label={tN('fields.browserNotifications.label')}
                fieldType="checkbox"
                checkToggle
                disabled={browserDisabled}
                note={browserNote}
              />
            </StyledFormGroup>
          </StyledCard.Box>
          <br />
          <StyledRow center>
            <StyledButton type="submit" disabled={!isValid || !props.dirty} primary>
              {t('submit')}
            </StyledButton>
          </StyledRow>
        </StyledForm>
      </Theme>
    </StyledContainer>
  );
};

PreferencesForm.propTypes = {
  allActionTypeTemplates: PropTypes.models.ActionTypeTemplatesStore.isRequired,
  allActionTypeMappings: PropTypes.models.ActionTypeMappingsStore.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  errors: PropTypes.object.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  isValid: PropTypes.bool.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  values: PropTypes.object.isRequired,
  setValues: PropTypes.func.isRequired,
  dirty: PropTypes.bool.isRequired,
};
